import { apiClient } from './client'
import type { User, UserCreate, UserUpdate, PaginatedResponse } from '@/types'

export interface ListUsersParams {
  page?: number
  per_page?: number
  search?: string
  role?: string
  is_active?: boolean
}

export async function listUsers(params: ListUsersParams = {}): Promise<PaginatedResponse<User>> {
  const response = await apiClient.get<PaginatedResponse<User>>('/users', { params })
  return response.data
}

export async function getUser(userId: string): Promise<User> {
  const response = await apiClient.get<User>(`/users/${userId}`)
  return response.data
}

export async function createUser(data: UserCreate): Promise<User> {
  const response = await apiClient.post<User>('/users', data)
  return response.data
}

export async function updateUser(userId: string, data: UserUpdate): Promise<User> {
  const response = await apiClient.put<User>(`/users/${userId}`, data)
  return response.data
}

export async function deactivateUser(userId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}`)
}

export async function reactivateUser(userId: string): Promise<User> {
  const response = await apiClient.post<User>(`/users/${userId}/reactivate`)
  return response.data
}

export async function listManagers(): Promise<User[]> {
  const response = await apiClient.get<User[]>('/users/managers/list')
  return response.data
}

export async function syncSlackId(userId: string): Promise<User> {
  const response = await apiClient.post<User>(`/users/${userId}/sync-slack`)
  return response.data
}

export async function deleteUserPermanently(userId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}/permanent`)
}

// Bulk update
export interface BulkUpdateRequest {
  user_ids: string[]
  updates: UserUpdate
}

export interface BulkUpdateResult {
  updated_count: number
}

export async function bulkUpdateUsers(data: BulkUpdateRequest): Promise<BulkUpdateResult> {
  const response = await apiClient.post<BulkUpdateResult>('/users/bulk-update', data)
  return response.data
}

export async function setTestUsers(userIds: string[]): Promise<void> {
  await apiClient.put('/users/test-users', { user_ids: userIds })
}

// Slack integration types
export interface SlackUserInfo {
  slack_id: string
  email: string
  name: string
  avatar_url: string | null
  timezone: string | null
  is_imported: boolean
  managition_user_id: string | null
}

export interface BulkSyncResult {
  synced_count: number
  not_found_count: number
  already_synced_count: number
  deactivated_count: number
  synced_users: string[]
  not_found_emails: string[]
  deactivated_users: string[]
}

export interface SlackImportRequest {
  slack_ids: string[]
  role: string
  timezone_override?: string  // If set, overrides individual Slack timezones
}

// Slack integration functions
export async function getSlackWorkspaceUsers(): Promise<SlackUserInfo[]> {
  const response = await apiClient.get<SlackUserInfo[]>('/users/slack/workspace-users')
  return response.data
}

export async function syncAllSlackIds(): Promise<BulkSyncResult> {
  const response = await apiClient.post<BulkSyncResult>('/users/slack/sync-all')
  return response.data
}

export async function importSlackUsers(data: SlackImportRequest): Promise<User[]> {
  const response = await apiClient.post<User[]>('/users/slack/import', data)
  return response.data
}
